import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type TimerVariant = 'bar' | 'digits';

/**
 * A turn countdown, as either a depleting bar or a digital readout.
 *
 * Purely presentational — it does NOT tick on its own. The design
 * system's own Timer owns an interval and counts itself down, which is
 * wrong for this game: a turn deadline is server state
 * (`turn_started_at` + `turn_seconds_limit`), and a client that keeps
 * its own count drifts away from the server that will actually expire
 * the turn. The caller derives `seconds` from the server's clock and
 * this renders whatever it is handed.
 *
 * `total` only sets the scale the fill and the urgency thresholds are
 * measured against — at or below half it reads warn, at or below a
 * fifth it reads critical and starts blinking.
 */
@Component({
  selector: 'hh-timer',
  standalone: true,
  template: `
    @if (variant() === 'digits') {
      <div [class]="digitClasses()">
        <span class="hh-timer__value">{{ display() }}</span>
        @if (total() < 60) {
          <span class="hh-timer__unit">SEC</span>
        }
      </div>
    } @else {
      <div
        [class]="barClasses()"
        role="progressbar"
        aria-valuemin="0"
        [attr.aria-valuemax]="total()"
        [attr.aria-valuenow]="seconds()"
        [attr.aria-valuetext]="seconds() + ' seconds left'"
      >
        <div class="hh-timer__fill" [style.width.%]="percent()"></div>
        <div class="hh-timer__dither" aria-hidden="true"></div>
      </div>
    }
  `,
  styleUrl: './hh-timer.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HhTimerComponent {
  readonly variant = input<TimerVariant>('bar');

  /** Seconds the turn started with — the scale, not a countdown source. */
  readonly total = input(30);

  /** Seconds remaining, owned by the caller (see the class doc). */
  readonly seconds = input(0);

  protected readonly percent = computed(() => {
    const total = this.total();
    if (total <= 0) return 0;
    return Math.max(0, Math.min(1, this.seconds() / total)) * 100;
  });

  /** ok | warn | crit, at the design system's own thresholds. */
  protected readonly level = computed(() => {
    const pct = this.percent() / 100;
    return pct <= 0.2 ? 'crit' : pct <= 0.5 ? 'warn' : 'ok';
  });

  protected readonly display = computed(() => {
    const left = Math.max(0, this.seconds());
    const pad = (n: number) => String(n).padStart(2, '0');
    // Under a minute the whole readout is the seconds, with a SEC unit
    // beside it; past that it needs the m:ss shape to stay readable.
    return this.total() >= 60
      ? `${Math.floor(left / 60)}:${pad(left % 60)}`
      : pad(left);
  });

  protected readonly barClasses = computed(() => `hh-timer__bar hh-timer__bar--${this.level()}`);

  protected readonly digitClasses = computed(
    () => `hh-timer__digits hh-timer__digits--${this.level()}`,
  );
}
